import { Link } from '@tanstack/react-router';
import { useTranslation } from '@eon/i18n';
import { hooks, type IdTag } from '@eon/api-client';
import { SelectField, SelectFieldOption, Skeleton } from '@eon/ui';

export interface IdTagPickerProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
  placeholder?: string;
  /** Values already picked elsewhere (e.g. Local Auth rows) — shown disabled. */
  excludeValues?: string[];
}

function tagDescription(tag: IdTag, t: (k: string) => string): string {
  if (tag.description?.trim()) return tag.description.trim();
  if (!tag.isPhysical) return t('deviceControl.idTag.digitalAuto');
  return tag.value;
}

function tagSubtitle(tag: IdTag): string {
  const parts = [tag.value];
  if (tag.ownerName) parts.push(tag.ownerName);
  if (tag.tagType) parts.push(tag.tagType);
  return parts.join(' · ');
}

export function IdTagPicker({
  value,
  onValueChange,
  disabled,
  size = 'md',
  placeholder,
  excludeValues = [],
}: IdTagPickerProps) {
  const { t } = useTranslation();
  const q = hooks.useAllAdminIdTags();
  const excluded = new Set(excludeValues.filter(Boolean));

  if (q.isLoading) {
    return <Skeleton className={size === 'sm' ? 'h-9 w-full' : 'h-10 w-full'} />;
  }

  if (q.isError) {
    return (
      <p className="text-destructive text-xs">
        {t('deviceControl.idTag.loadError')}{' '}
        <button type="button" className="underline" onClick={() => q.refetch()}>
          {t('deviceControl.idTag.retry')}
        </button>
      </p>
    );
  }

  const tags = q.data ?? [];

  if (tags.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        {t('deviceControl.idTag.empty')}{' '}
        <Link to="/id-tags" className="text-primary underline">
          {t('deviceControl.idTag.manage')}
        </Link>
      </p>
    );
  }

  return (
    <SelectField
      value={value || undefined}
      onValueChange={onValueChange}
      disabled={disabled}
      size={size}
      placeholder={placeholder ?? t('deviceControl.idTag.placeholder')}
    >
      {tags.map((tag) => {
        const taken = excluded.has(tag.value) && tag.value !== value;
        const inactive = tag.status !== 'active';
        return (
          <SelectFieldOption
            key={tag.id}
            value={tag.value}
            disabled={inactive || taken}
            description={
              <>
                <span className="font-mono">{tagSubtitle(tag)}</span>
                {tagDescription(tag, t) !== tag.value ? (
                  <span className="mt-0.5 block">{tagDescription(tag, t)}</span>
                ) : null}
                {inactive ? (
                  <span className="mt-0.5 block">{t(`deviceControl.idTag.status.${tag.status}`)}</span>
                ) : null}
                {taken ? <span className="mt-0.5 block">{t('deviceControl.idTag.alreadyUsed')}</span> : null}
              </>
            }
          >
            {tag.name?.trim() || tag.value}
          </SelectFieldOption>
        );
      })}
    </SelectField>
  );
}
